import { InjectionToken } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, timer } from 'rxjs';
import { exhaustMap, takeUntil, takeWhile, tap } from 'rxjs/operators';

import { PollingConfig, PollingError } from '@pe/checkout/types';

export const POLLING_CONFIG = new InjectionToken<PollingConfig>('Polling configuration', {
  providedIn: 'root',
  factory: () => DEFAULT_POLLING_CONFIG,
});

export const DEFAULT_POLLING_CONFIG: PollingConfig = {
  pollingInterval: 5000,
  maxTimeout: 180000,
};

export function pollWhile<T>(
  config: PollingConfig,
  guardFn: (value: T) => boolean,
  throwError = true,
): MonoTypeOperatorFunction<T> {
  return source$ => new Observable<T>((subscriber) => {
    let lastValue: T | undefined;
    let guardCompleted = false;

    const sub = timer(0, config.pollingInterval).pipe(
      takeUntil(timer(config.maxTimeout)),
      exhaustMap(() => source$),
      tap((v) => { lastValue = v }),
      takeWhile(v => guardFn(v) || !(guardCompleted = true), true),
    ).subscribe({
      next: v => subscriber.next(v),
      error: e => subscriber.error(e),
      complete: () => {
        if (guardCompleted) {
          return subscriber.complete();
        }
        if (lastValue !== undefined) {
          subscriber.next(lastValue);
        }
        throwError
          ? subscriber.error(new PollingError('timeout', 'Polling timeout!'))
          : subscriber.complete();
      },
    });

    return () => sub.unsubscribe();
  });
}

export function isPollingError(error: Object): error is PollingError {
  return error instanceof PollingError;
}
